import React, { useState, useMemo, useCallback } from 'react';
import { Users, Sparkles } from 'lucide-react';
import { UsersFilters } from '@/hooks/queries/useUsers';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from '@/hooks/useTranslation';
import PeopleFiltersBar from '@/components/people/PeopleFiltersBar';
import PeopleContent from '@/components/people/PeopleContent';
import PageBackground from '@/components/common/PageBackground';

const PeoplePage: React.FC = () => {
  const { t } = useTranslation('people');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | undefined>();

  const filters: UsersFilters = useMemo(
    () => ({
      limit: 12,
      departmentId: selectedDepartmentId === 'all' ? undefined : selectedDepartmentId,
      search: debouncedSearch || undefined,
      sortBy: 'firstName',
      sortOrder: 'asc' as const,
    }),
    [selectedDepartmentId, debouncedSearch],
  );

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedDepartmentId(undefined);
  }, []);

  const hasActiveFilters = !!(selectedDepartmentId || searchQuery);

  return (
    <PageBackground>
      {/* Modern Header */}
      <div className="mb-12 text-center sm:text-left">
        <div className="inline-flex items-center gap-3 mb-4">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50" />
            <div className="relative p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
              <Users className="w-8 h-8 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <div className="flex items-center gap-2 mt-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{t('subtitle')}</p>
            </div>
          </div>
        </div>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mt-4 leading-relaxed">{t('description')}</p>
      </div>

      {/* Filters */}
      <PeopleFiltersBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedDepartmentId={selectedDepartmentId}
        onDepartmentChange={setSelectedDepartmentId}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* People Content */}
      <PeopleContent filters={filters} hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
    </PageBackground>
  );
};

export default PeoplePage;
